import {Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {Store} from '@ngrx/store';
import {ConfirmDialogComponent} from '../confirm-dialog/confirm-dialog.component';
import {Department, EmployeeJobCode, IEmployee, IHoliday, ISRDetail, IWeek} from '@signal/asp-data-commons';
import {ScheduleRotationState} from '../../../store/schedule-rotation/schedule-rotation.reducer';
import * as fromTeamSelectors from '../../../store/team/team.selectors';
import * as fromScheduleRotationActions from '../../../store/schedule-rotation/schedule-rotation.actions';
import {TeamState} from '../../../store/team/team.reducer';
import {SubscriptionList} from '../../models/asp.types';
import {EmployeeState} from '../../../store/employee/employee.reducer';
import {selectEmployeeForSchedule} from '../../../store/employee/employee.selectors';
import {Predicate} from '@ngrx/entity';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {  CdkDragMove } from '@angular/cdk/drag-drop';
import {TranslateService} from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { HoursOfOperation, Settings } from '../../models/settings.model';
import { SettingsState } from '../../../store/settings/settings.reducer';
import * as fromActions from '../../../store/settings/settings.actions';
import * as fromSelector from '../../../store/settings/settings.selectors';
import { HolidayState } from '../../../store/holiday/holiday.reducer';
import * as HolidayActions from '../../../store/holiday/holiday.actions';
import { selectHolidays } from 'src/app/modules/store/holiday/holiday.adapter';
import { unsubscribeSubscriptions } from '../../services/util.service';

const speed = 10;

@Component({
  selector: 'app-schedule-rotation',
  templateUrl: './schedule-rotation.component.html',
  styleUrls: ['./schedule-rotation.component.scss']
})
export class ScheduleRotationComponent implements OnInit, OnChanges, OnDestroy {

  @Input() weeksData : IWeek[];
  @Input() config : { department: Department, id: string};
  @Input() weekNo : number;
  animationFrame: number | undefined;
  @ViewChild('scheduleManagmentContainer')
  scheduleManagmentContainer:ElementRef<HTMLElement>;

  sourceItems: ISRDetail[];
  subs: SubscriptionList = {};
  subHeading: string;
  emptyText1: string;
  emptyText2: string;
  weekDays =[{day: "monday", isClosed: false},{day: "tuesday", isClosed: false},{day: "wednesday", isClosed: false},{day: "thursday", isClosed: false},
  {day: "friday", isClosed: false},{day: "saturday", isClosed: false},{day: "sunday", isClosed: false}];
  //weekDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  settings$: Observable<Settings>;
  holidays$: Observable<IHoliday[]>;
  hoursOfOperation: HoursOfOperation[] = [];
  closedDay:string[];
  closeday:string[];
  private deleteConfig = {
    width: '400px',
    height: '220px',
    panelClass: 'asp-modal-container'
  };
  shiftForm : UntypedFormGroup;
  shiftsArray : UntypedFormArray;
  shiftsToShow : Array<object[]> = [];
  weeksPanelStatus : boolean[];
  constructor(public dialog: MatDialog,
              private readonly teamState: Store<TeamState>,
              private readonly employeeState: Store<EmployeeState>,
              private readonly scheduleRotationState: Store<ScheduleRotationState>,
              private settingsState: Store<SettingsState>,
              private readonly holidayState: Store<HolidayState>,
              private readonly translate:TranslateService) {
  }

  ngOnInit(): void {
    this.setTextFields();
    this.setupStore();
    this.weeksPanelStatus = new Array(this.weeksData.length).fill(false);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('config')) {
      this.setTextFields();
    }
  }

  onDelete(index: any) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, this.deleteConfig);
    this.subs.dialog = dialogRef.afterClosed().subscribe(event => {
      if (event.action === 'confirm') {
        this.deleteWeek(index);
      }
    });
  }

  itemsUpdated(ev: any[],list, index, shiftId: string) {
    const distinct = [];
    const unique = {};
    for (const e of ev) {
      if (!unique[e.id]) {
        distinct.push(e);
        unique[e.id] = 1;
      }
    }
    this.scheduleRotationState.dispatch(new fromScheduleRotationActions.UpdateScheduleRotation({
      department: this.config?.department,
      id: this.config.id,
      weeklyRotations: distinct,
      index: index,
      day: list,
      shiftId
    }));
  }

  deleteWeek(weekNum:number){
    this.scheduleRotationState.dispatch(new fromScheduleRotationActions.DeleteWeek({
      id: this.config?.id,
      department: this.config?.department,
      weekIndex: weekNum
    }));
  }

  addWeek() {
    this.scheduleRotationState.dispatch(new fromScheduleRotationActions.AddWeek({
      id: this.config?.id,
      department: this.config?.department
    }));
  }

  getItems(currentShift,weekNum:number,day:string){
    if(this.weeksData[weekNum][day].length > 0){
      let details = [];
      this.weeksData[weekNum][day].forEach(shift=>{
        if(shift['shiftId'] === currentShift.shiftId){
          details = shift.detail;
        }
      });
      return (Array.isArray(details) && details) ? [...details] : [];
    }
    return [];
  }

  private setTextFields() {
    if (this.config?.department === Department.SALES) {
      this.subHeading = 'otherTexts.salesAdvisorsPool';
      this.emptyText1 = this.translate.instant('otherTexts.thereAreNoAdvisors');
      this.emptyText2 = this.translate.instant('otherTexts.dragServiceAdvisorsFromTheSidebar');

      const activeAdvisorPredicate: Predicate<IEmployee> =
        (e: IEmployee) => e.department === Department.SALES
          && e.jobCode === EmployeeJobCode.ADVISOR
          && e.isActive;
      this.subs.sourceSub = this.employeeState.select(selectEmployeeForSchedule, {predicate: activeAdvisorPredicate})
        .subscribe(data => this.sourceItems = [...data]);
    } else {
      this.subs.sourceSub = this.teamState.select(fromTeamSelectors.selectTeamsForSchedule)
        .subscribe(data => this.sourceItems = [...data]);
        this.subHeading = 'scheduleRotation.teams';
        this.emptyText1 = this.translate.instant('otherTexts.thereAreNoTeams');
        this.emptyText2 = this.translate.instant('otherTexts.dragTeamsFromTheSidebar');  
    }
    this.shiftForm = new UntypedFormGroup({
      shifts : new UntypedFormArray([])
    })
    this.subs.shiftChangeSub = this.shiftForm.get('shifts').valueChanges.subscribe(value=>{
      this.filterShifts(value);
    })
    this.weeksData.forEach((week,index) => {
      this.shiftsArray = this.shiftForm.get('shifts') as UntypedFormArray;
      this.shiftsArray.push(
        new UntypedFormGroup({
          shiftId : new UntypedFormControl(this.getShiftId(index))
        })
      )
    });
  }
  getShiftId(index:number){
    if(this.shiftsToShow.length == 0){
      return "all";
    }
    if(this.shiftsToShow.length<index+1){
      return "all";
    }
    if(this.shiftsToShow[index].length==1){
      return this.shiftsToShow[index][0]['shiftId'];
    }
    return "all";
  }
  private setupStore() {
    const props = {department: this.config?.department};
    /*this.schedules$.subscribe(data => {
      this.scheduleData = data;
    })*/
    this.settingsState.dispatch(new fromActions.LoadSettings(props));
    this.settings$ = this.settingsState.select(fromSelector.selectSettings);
    this.subs.settings = this.settings$.subscribe(data =>{
      if(data && !!data.time){
       this.hoursOfOperation = data?.time.hoursOfOperation;
      }
      this.weekDays.forEach(item =>{
       const daysclosed = this.hoursOfOperation.filter(x=> x.operation == 'Closed').map(y=>{
          if(item.day.includes(y.day.toLowerCase())){
            item.isClosed = true;
          }
        })
      })
    });
    const holObj = {
      activeYear:new Date().getFullYear().toString(),
      department: this.config?.department,
    }
    this.holidayState.dispatch(new HolidayActions.LoadHolidays(holObj));
    this.holidays$ = this.holidayState.select(selectHolidays);
  }

  filterShifts(weeklyShifts : Array<object>){
    weeklyShifts.forEach((week,index)=>{
      if(week['shiftId'] == 'all'){
        this.shiftsToShow[index] = this.weeksData[index][this.weekDays[0].day];
      }else{
        this.shiftsToShow[index] = this.weeksData[index][this.weekDays[0].day].filter(shifts=>{
          return shifts['shiftId'] == week['shiftId'];
        });
      }
    });
  }

  /*triggerscroll based on movement cdk move*/
  cdkDragStarted(event){  
    this.subs.drag = event.source.moved.subscribe(cdkmoveevent=>{
      this.triggerScroll(cdkmoveevent);
    });
  }

  public triggerScroll($event: CdkDragMove) {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = undefined;
    }
    this.animationFrame = requestAnimationFrame(() => this.scrollMainContainer($event));
  }

  private scrollMainContainer($event: CdkDragMove) {
    const { y } = $event.pointerPosition;
    const baseEl = this.scheduleManagmentContainer.nativeElement.parentElement.parentElement.parentElement.parentElement
                                                                .parentElement.parentElement.parentElement.parentElement
                                                                .parentElement.parentElement;
    const box = baseEl.getBoundingClientRect();
    const scrollTop = baseEl.scrollTop;
    const top = box.top + - y ;
    if (top > 0 && scrollTop !== 0) {
      const newScroll = scrollTop - speed * Math.exp(top / 50);
      baseEl.scrollTop = newScroll;
      this.animationFrame = requestAnimationFrame(() => this.scrollMainContainer($event));
      return;
    }
    const bottom = y - box.bottom ;
    if (bottom > 0 && scrollTop < box.bottom || scrollTop>574.5) {
      const newScroll = scrollTop + speed * Math.exp(bottom / 50);
      baseEl.scrollTop = newScroll;
      this.animationFrame = requestAnimationFrame(() => this.scrollMainContainer($event));
    }
  }
  getTranslation(value){
    return value ? this.translate.instant(`common.${value}`) : value;
  }   

  ngOnDestroy() {
    unsubscribeSubscriptions(this.subs);
  }
}
